import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SiteNav } from './SiteNav';
import {
  ArrowRight, ArrowLeft, Check, Plus, Trash2, Upload, FileText,
  Home, ChevronDown, ChevronUp, Ruler, DoorOpen
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ScopeEntryGate from './scope/ScopeEntryGate';
import SCOPEVisionFlow from './scope/SCOPEVisionFlow';
import './DetailedTool.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PC_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

const ROOM_PRESETS = [
  { type: 'living', label: 'Living Room', icon: '🛋️' },
  { type: 'kitchen', label: 'Kitchen', icon: '🍳' },
  { type: 'bedroom', label: 'Bedroom', icon: '🛏️' },
  { type: 'bathroom', label: 'Bathroom', icon: '🚿' },
  { type: 'hallway', label: 'Hallway / Landing', icon: '🚪' },
  { type: 'utility', label: 'Utility Room', icon: '🧺' },
  { type: 'ensuite', label: 'En-suite', icon: '🚿' },
  { type: 'wc', label: 'WC / Cloakroom', icon: '🚽' },
  { type: 'dining', label: 'Dining Room', icon: '🍽️' },
  { type: 'study', label: 'Study / Office', icon: '💻' },
  { type: 'other', label: 'Other', icon: '📐' },
];

const FLOOR_OPTIONS = [
  { id: 'carpet', label: 'Carpet', spec: '80/20 wool twist' },
  { id: 'engineered', label: 'Engineered wood', spec: 'Oak, click-fit, 4mm+ wear' },
  { id: 'lvt', label: 'LVT / Vinyl plank', spec: 'Luxury vinyl tile, click-fit' },
  { id: 'porcelain', label: 'Porcelain tile', spec: '600×600 rectified' },
  { id: 'ceramic', label: 'Ceramic tile', spec: '300×600' },
  { id: 'laminate', label: 'Laminate', spec: 'AC4+ commercial grade' },
  { id: 'existing', label: 'Retain existing', spec: 'No new floor finish' },
];

const WALL_OPTIONS = [
  { id: 'paint', label: 'Paint (2 coat)', spec: 'Emulsion on mist coat' },
  { id: 'replaster_paint', label: 'Replaster + paint', spec: 'Skim + 2 coat emulsion' },
  { id: 'tile_full', label: 'Full height tiling', spec: 'Ceramic/porcelain to all walls' },
  { id: 'tile_splashback', label: 'Splashback tiling only', spec: 'To wet areas / behind worktop' },
  { id: 'existing', label: 'Retain existing', spec: 'No wall works' },
];

const CEILING_OPTIONS = [
  { id: 'paint', label: 'Paint only', spec: '2 coat emulsion' },
  { id: 'replaster_paint', label: 'Overboard + paint', spec: 'New plasterboard + skim + paint' },
  { id: 'existing', label: 'Retain existing', spec: 'No ceiling works' },
];

const KITCHEN_SUPPLY = [
  { id: 'client', label: 'Client supply', desc: 'You buy kitchen units + appliances; we install' },
  { id: 'contractor', label: 'Contractor supply + fit', desc: 'We source and install everything' },
  { id: 'na', label: 'Not applicable', desc: 'No kitchen in this room' },
];

const KITCHEN_SPEC = [
  { id: 'budget', label: 'Budget', desc: 'Howdens / Wickes flat-pack, laminate worktop, basic integrated appliances' },
  { id: 'mid', label: 'Mid-range (default)', desc: 'Wren / Magnet / Symphony rigid shaker, composite or quartz worktop, good integrated appliances' },
  { id: 'high', label: 'High-end', desc: 'Premium shaker or handle-less, quartz worktop, premium integrated appliances' },
];

const BATHROOM_SUPPLY = [
  { id: 'client', label: 'Client supply', desc: 'You buy sanitaryware; we install' },
  { id: 'contractor', label: 'Contractor supply + fit', desc: 'We source and install everything' },
  { id: 'na', label: 'Not applicable', desc: 'No bathroom in this room' },
];

const createRoom = (type = 'living', label = '') => ({
  id: Date.now() + Math.random(),
  type,
  name: label || ROOM_PRESETS.find(p => p.type === type)?.label || 'Room',
  length: '',
  width: '',
  ceilingHeight: '2.4',
  doors: 1,
  windows: 0,
  floor: 'carpet',
  walls: 'paint',
  ceiling: 'paint',
  electrics: { sockets: 4, lights: 1, switches: 1 },
  kitchenSupply: 'na',
  kitchenSpec: 'mid',
  bathroomSupply: 'na',
  stripOut: false,
  notes: '',
  expanded: true,
});

export const DetailedTool = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  // Entry gate — shown on /scope landing for new visitors. Skip once the user
  // has picked a method or if we're resuming a draft via ?draft=ID.
  const [methodChosen, setMethodChosen] = useState(() => !!searchParams.get('draft'));
  const [showVision, setShowVision] = useState(false);

  // SCOPEvision handler: we need a draft_id before the upload endpoint will
  // accept a plan. Create one on demand if we don't already have one.
  const startSCOPEVision = async () => {
    if (!draftId) {
      try {
        const res = await axios.post(`${API}/scope/drafts`, { method: 'scopevision' });
        const newId = res.data?.draft?.draft_id || res.data?.draft_id;
        if (newId) setDraftId(newId);
      } catch (err) {
        console.warn('Could not create draft for SCOPEvision:', err?.message);
      }
    }
    setShowVision(true);
  };

  // Step 0: Project info
  const [projectName, setProjectName] = useState('');
  const [projectEmail, setProjectEmail] = useState('');
  const [postcode, setPostcode] = useState('');
  const [address, setAddress] = useState('');
  const [addressFull, setAddressFull] = useState('');
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [addressList, setAddressList] = useState([]);
  const [showAddressList, setShowAddressList] = useState(false);

  // Step 1: Rooms
  const [rooms, setRooms] = useState([createRoom('living', 'Living Room')]);

  // Step 2: Plan upload
  const [planFile, setPlanFile] = useState(null);

  // Step 3: Output
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState(null);

  // ── PHASE B: draft persistence (logged-in only) ──
  const [draftId, setDraftId] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [sessionLimit, setSessionLimit] = useState(null); // { current, limit, tier } when blocked
  const debounceRef = useRef(null);

  // Once user state is known, set email from account if logged in (can still override)
  useEffect(() => {
    if (user && !projectEmail) setProjectEmail(user.email);
  }, [user, projectEmail]);

  // Load draft if ?draft=ID is in URL, or auto-populate if user has a single in-progress draft
  useEffect(() => {
    if (draftLoaded || !user) return;
    const qsDraftId = searchParams.get('draft');
    if (qsDraftId) {
      (async () => {
        try {
          const res = await axios.get(`${API}/scope/drafts/${qsDraftId}`);
          const d = res.data;
          if (d && d.draft_id) {
            setDraftId(d.draft_id);
            if (d.address) setAddress(d.address);
            if (d.address_full) setAddressFull(d.address_full);
            if (d.postcode) setPostcode(d.postcode);
            if (d.address) setAddressConfirmed(true);
            if (Array.isArray(d.rooms) && d.rooms.length) setRooms(d.rooms);
            if (d.project_name) setProjectName(d.project_name);
          }
        } catch (err) {
          console.warn('Draft load failed:', err?.message);
        } finally {
          setDraftLoaded(true);
        }
      })();
    } else {
      // No explicit draft ID — silently prefill from the user's latest Indicative project
      // so a returning user doesn't re-type the address they just provided.
      (async () => {
        try {
          const res = await axios.get(`${API}/client/orders`);
          const orders = res.data?.orders || [];
          const latestIndicative = orders.find(o => o.product_type === 'indicative' && o.address);
          if (latestIndicative) {
            if (latestIndicative.address) setAddress(latestIndicative.address);
            if (latestIndicative.address_full) setAddressFull(latestIndicative.address_full);
            if (latestIndicative.postcode) setPostcode(latestIndicative.postcode);
            if (latestIndicative.address) setAddressConfirmed(true);
          }
        } catch {
          /* best-effort prefill; silent failure */
        } finally {
          setDraftLoaded(true);
        }
      })();
    }
  }, [user, searchParams, draftLoaded]);

  // Debounced autosave: save/upsert draft whenever rooms/address change, for logged-in users
  const saveDraft = useCallback(async () => {
    if (!user) return; // only logged-in users persist drafts
    if (!address && !postcode && rooms.length <= 1) return; // nothing meaningful yet
    try {
      const res = await axios.post(`${API}/scope/drafts`, {
        draft_id: draftId,
        address, address_full: addressFull, postcode,
        scope_level: 'detailed',
        project_name: projectName,
        rooms,
      });
      if (res.data?.draft?.draft_id && !draftId) setDraftId(res.data.draft.draft_id);
      setSessionLimit(null);
    } catch (err) {
      if (err.response?.status === 402) {
        setSessionLimit(err.response.data);
      }
    }
  }, [user, draftId, address, addressFull, postcode, projectName, rooms]);

  useEffect(() => {
    if (!draftLoaded || !user) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(saveDraft, 1200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [saveDraft, draftLoaded, user]);

  // ── Address lookup ──
  const postcodeValid = PC_RE.test(postcode);

  const lookupAddresses = async () => {
    if (!postcodeValid) return;
    try {
      const res = await axios.get(`${API}/address/lookup?postcode=${encodeURIComponent(postcode)}`);
      if (res.data.found && res.data.addresses?.length) {
        setAddressList(res.data.addresses);
      } else {
        setAddressList([{ line1: postcode, city: '', postcode: postcode, full: postcode }]);
      }
    } catch (err) {
      console.error('Address lookup error:', err?.message);
      setAddressList([{ line1: postcode, city: '', postcode: postcode, full: postcode }]);
    }
    setShowAddressList(true);
  };

  const selectAddress = (addr) => {
    setAddress(addr.line1);
    setAddressFull(addr.full);
    setAddressConfirmed(true);
    setShowAddressList(false);
  };

  // ── Room management ──
  const addRoom = (type) => {
    setRooms(prev => [...prev, createRoom(type)]);
  };

  const updateRoom = (id, field, value) => {
    setRooms(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const updateElectrics = (id, field, value) => {
    setRooms(prev => prev.map(r => r.id === id ? { ...r, electrics: { ...r.electrics, [field]: value } } : r));
  };

  const removeRoom = (id) => {
    setRooms(prev => prev.filter(r => r.id !== id));
  };

  const toggleExpand = (id) => {
    setRooms(prev => prev.map(r => r.id === id ? { ...r, expanded: !r.expanded } : r));
  };

  const roomArea = (r) => {
    const l = parseFloat(r.length) || 0;
    const w = parseFloat(r.width) || 0;
    return l * w;
  };

  const totalArea = rooms.reduce((sum, r) => sum + roomArea(r), 0);

  // ── Calculate ──
  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/detailed/calculate`, {
        postcode, address: addressFull, rooms: rooms.map(r => ({
          name: r.name, type: r.type, length: parseFloat(r.length) || 0,
          width: parseFloat(r.width) || 0, ceiling_height: parseFloat(r.ceilingHeight) || 2.4,
          doors: r.doors, windows: r.windows, floor: r.floor, walls: r.walls,
          ceiling: r.ceiling, electrics: r.electrics, kitchen_supply: r.kitchenSupply,
          kitchen_spec: r.kitchenSpec || 'mid',
          bathroom_supply: r.bathroomSupply, strip_out: r.stripOut, notes: r.notes,
        })),
      });
      setResult(res.data);
    } catch (err) {
      console.error(err);
    }
    setCalculating(false);
  };

  // ── Checkout — 3 tiers ──
  const TIERS = {
    scope_materials: { price: 29, label: 'Materials Schedule' },
    scope_sow:       { price: 119,   label: 'Schedule of Works' },
    scope_full:      { price: 199,   label: 'Full Project Pack' },
  };
  const handleCheckout = async (tier) => {
    if (!TIERS[tier]) return;
    setCheckoutTier(tier);
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${API}/scope/tier-checkout`, {
        tier,
        draft_id: draftId,
        email: projectEmail,
        address: addressFull || address,
        postcode,
        scope_level: 'detailed',
        output_range_low: result?.total_low || 0,
        output_range_high: result?.total_high || 0,
        region: result?.region || '',
        location_factor: result?.location_factor || 1,
        rooms_data: rooms.map(r => ({ name: r.name, type: r.type, area: roomArea(r) })),
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      console.error('Checkout error:', err?.message);
      alert(err?.response?.data?.error || 'Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
      setCheckoutTier(null);
    }
  };

  const goStep = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Validation
  const step0Valid = projectName && projectEmail && projectEmail.includes('@') && addressConfirmed;
  const step1Valid = rooms.length > 0 && rooms.every(r => roomArea(r) > 0);

  return (
    <div className="dt-app" data-testid="detailed-tool">
      <SiteNav cta="home" />
      <div className="dt-body">
        {sessionLimit && (
          <div
            data-testid="session-limit-banner"
            style={{ maxWidth: 960, margin: '20px auto 0', padding: '14px 18px', background: '#FEF3C7', border: '1.5px solid #FCD34D', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}
          >
            <div style={{ fontSize: 13, color: '#713F12', lineHeight: 1.5 }}>
              <strong>You have {sessionLimit.limit} saved projects — the maximum on a free account.</strong><br />
              Archive or delete an existing project in your account, or upgrade for unlimited projects.
            </div>
            <button
              onClick={() => navigate('/account')}
              style={{ background: '#0F2742', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >Open My Account →</button>
          </div>
        )}
        {user && draftId && draftLoaded && (
          <div
            data-testid="draft-resumed-strip"
            style={{ maxWidth: 960, margin: '20px auto 0', padding: '10px 18px', background: '#ECFDF5', border: '1.5px solid #A7F3D0', borderRadius: 10, fontSize: 12, color: '#065F46' }}
          >
            Welcome back — we saved your progress. Pick up where you left off.
          </div>
        )}

        {/* Progress — hidden while the entry gate is still the active screen */}
        {(step > 0 || methodChosen) && (
          <div className="dt-steps">
            {['Project Info', 'Rooms and Finishes', 'Upload Plan', 'Review and Pay'].map((label, i) => (
              <div key={label} className={`dt-step-item${i < step ? ' done' : i === step ? ' active' : ''}`}>
                <div className="dt-step-num">{i < step ? <Check className="w-3 h-3" /> : i + 1}</div>
                <span className="dt-step-label">{label}</span>
              </div>
            ))}
          </div>
        )}

        {/* ═══ STEP 0: Project Info ═══ */}
        {step === 0 && !methodChosen && !showVision && (
          <div style={{ maxWidth: 1120, margin: '28px auto 0', padding: '0 16px' }} data-testid="scope-entry-wrap">
            <ScopeEntryGate
              open={!methodChosen && !showVision}
              onUploadPlan={() => navigate('/scope/draw')}
              onDimensions={() => setMethodChosen(true)}
              onSCOPEVision={startSCOPEVision}
            />
          </div>
        )}

        {step === 0 && showVision && (
          <div style={{ maxWidth: 1120, margin: '28px auto 0', padding: '0 16px' }} data-testid="scopevision-wrap">
            <SCOPEVisionFlow
              draftId={draftId}
              onBack={() => setShowVision(false)}
              onUseDimensions={() => { setShowVision(false); setMethodChosen(true); }}
              onComplete={() => navigate(`/scope/draw?draft=${draftId}`)}
            />
          </div>
        )}

        {step === 0 && methodChosen && (
          <div className="dt-screen" data-testid="step-0">
            <div className="dt-card">
              <div className="dt-card-hdr">
                <h2>Let's get a more defined estimate for your project</h2>
                <p>A few quick details about your property so we can price the work accurately.</p>
              </div>
              <div className="dt-card-body">
                <div className="dt-grid-2">
                  <div className="dt-field">
                    <label>Your name *</label>
                    <input className="dt-input" value={projectName} onChange={e => setProjectName(e.target.value)} data-testid="project-name" />
                  </div>
                  <div className="dt-field">
                    <label>Email *</label>
                    <input className="dt-input" type="email" value={projectEmail} onChange={e => setProjectEmail(e.target.value)} data-testid="project-email" />
                  </div>
                </div>

                <div className="dt-field">
                  <label>Postcode *</label>
                  <div style={{ position: 'relative' }}>
                    <input className="dt-input" style={{ textTransform: 'uppercase' }} placeholder="e.g. M21 9WQ" value={postcode}
                      onChange={e => { setPostcode(e.target.value.toUpperCase()); setAddressConfirmed(false); setShowAddressList(false); }}
                      onKeyDown={e => e.key === 'Enter' && lookupAddresses()} data-testid="dt-postcode" />
                    {postcodeValid && <div className="dt-pc-tick"><Check className="w-4 h-4" /></div>}
                  </div>
                </div>

                {postcodeValid && !addressConfirmed && !showAddressList && (
                  <button className="dt-btn dt-btn-navy" onClick={lookupAddresses} data-testid="dt-find-addr">
                    Find address
                  </button>
                )}

                {showAddressList && !addressConfirmed && (
                  <div className="dt-addr-list" data-testid="dt-addr-list">
                    {addressList.map((a, i) => (
                      <button key={`${a.line1}-${a.postcode}-${i}`} className="dt-addr-opt" onClick={() => selectAddress(a)} data-testid={`dt-addr-${i}`}>
                        {a.line1}<span className="dt-addr-sub">{a.city ? `${a.city}, ` : ''}{a.postcode}</span>
                      </button>
                    ))}
                  </div>
                )}

                {addressConfirmed && (
                  <div className="dt-confirmed" data-testid="dt-addr-confirmed">
                    <Check className="w-4 h-4" style={{ color: '#16A34A' }} />
                    <div>
                      <div className="dt-confirmed-addr">{address}</div>
                      <button className="dt-link" onClick={() => { setAddressConfirmed(false); setShowAddressList(false); }}>Change</button>
                    </div>
                  </div>
                )}

                <div className="dt-nav">
                  <div />
                  <button className="dt-btn dt-btn-accent" disabled={!step0Valid} onClick={() => goStep(1)} data-testid="step0-next">
                    Define rooms <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 1: Rooms & Finishes ═══ */}
        {step === 1 && (
          <div className="dt-screen" data-testid="step-1">
            <div className="dt-card">
              <div className="dt-card-hdr">
                <h2>Rooms and Finishes</h2>
                <p>Add each room with dimensions, then select finishes. We'll calculate quantities automatically.</p>
              </div>
              <div className="dt-card-body">
                {/* Summary bar */}
                <div className="dt-summary-bar">
                  <span><Home className="w-3.5 h-3.5 inline" /> {rooms.length} room{rooms.length !== 1 ? 's' : ''}</span>
                  <span><Ruler className="w-3.5 h-3.5 inline" /> {totalArea.toFixed(1)} m² total</span>
                  <span><DoorOpen className="w-3.5 h-3.5 inline" /> {rooms.reduce((s, r) => s + r.doors, 0)} doors</span>
                </div>

                {/* Room cards */}
                <div className="dt-rooms">
                  {rooms.map((room, idx) => {
                    const area = roomArea(room);
                    const preset = ROOM_PRESETS.find(p => p.type === room.type);
                    const isWet = ['bathroom', 'ensuite', 'wc', 'kitchen', 'utility'].includes(room.type);
                    return (
                      <div key={room.id} className={`dt-room${room.expanded ? ' expanded' : ''}`} data-testid={`room-${idx}`}>
                        {/* Room header */}
                        <div className="dt-room-hdr" onClick={() => toggleExpand(room.id)}>
                          <div className="dt-room-hdr-left">
                            <span className="dt-room-icon">{preset?.icon || '📐'}</span>
                            <div>
                              <input className="dt-room-name" value={room.name}
                                onClick={e => e.stopPropagation()}
                                onChange={e => updateRoom(room.id, 'name', e.target.value)} />
                              <div className="dt-room-meta">{area > 0 ? `${area.toFixed(1)} m²` : 'No dimensions yet'}</div>
                            </div>
                          </div>
                          <div className="dt-room-hdr-right">
                            <button className="dt-icon-btn" onClick={e => { e.stopPropagation(); removeRoom(room.id); }}><Trash2 className="w-3.5 h-3.5" /></button>
                            {room.expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </div>

                        {/* Room body */}
                        {room.expanded && (
                          <div className="dt-room-body">
                            {/* Dimensions */}
                            <div className="dt-section-label">Dimensions</div>
                            <div className="dt-grid-3">
                              <div className="dt-field-sm">
                                <label>Length (m)</label>
                                <input className="dt-input-sm" type="number" step="0.1" value={room.length}
                                  onChange={e => updateRoom(room.id, 'length', e.target.value)} data-testid={`room-${idx}-length`} />
                              </div>
                              <div className="dt-field-sm">
                                <label>Width (m)</label>
                                <input className="dt-input-sm" type="number" step="0.1" value={room.width}
                                  onChange={e => updateRoom(room.id, 'width', e.target.value)} data-testid={`room-${idx}-width`} />
                              </div>
                              <div className="dt-field-sm">
                                <label>Ceiling (m)</label>
                                <input className="dt-input-sm" type="number" step="0.1" value={room.ceilingHeight}
                                  onChange={e => updateRoom(room.id, 'ceilingHeight', e.target.value)} />
                              </div>
                            </div>
                            <div className="dt-grid-2" style={{ marginTop: 8 }}>
                              <div className="dt-field-sm">
                                <label>Doors</label>
                                <div className="dt-counter">
                                  <button onClick={() => updateRoom(room.id, 'doors', Math.max(0, room.doors - 1))}>−</button>
                                  <span>{room.doors}</span>
                                  <button onClick={() => updateRoom(room.id, 'doors', room.doors + 1)}>+</button>
                                </div>
                              </div>
                              <div className="dt-field-sm">
                                <label>Windows</label>
                                <div className="dt-counter">
                                  <button onClick={() => updateRoom(room.id, 'windows', Math.max(0, room.windows - 1))}>−</button>
                                  <span>{room.windows}</span>
                                  <button onClick={() => updateRoom(room.id, 'windows', room.windows + 1)}>+</button>
                                </div>
                              </div>
                            </div>

                            {/* Strip out */}
                            <div className="dt-strip-toggle">
                              <label className="dt-toggle-label">
                                <input type="checkbox" checked={room.stripOut} onChange={e => updateRoom(room.id, 'stripOut', e.target.checked)} />
                                Full strip out required
                              </label>
                            </div>

                            {/* Finishes */}
                            <div className="dt-section-label" style={{ marginTop: 12 }}>Finishes</div>
                            <div className="dt-finish-grid">
                              <div className="dt-field-sm">
                                <label>Floor</label>
                                <select className="dt-select" value={room.floor} onChange={e => updateRoom(room.id, 'floor', e.target.value)}>
                                  {FLOOR_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                              </div>
                              <div className="dt-field-sm">
                                <label>Walls</label>
                                <select className="dt-select" value={room.walls} onChange={e => updateRoom(room.id, 'walls', e.target.value)}>
                                  {WALL_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                              </div>
                              <div className="dt-field-sm">
                                <label>Ceiling</label>
                                <select className="dt-select" value={room.ceiling} onChange={e => updateRoom(room.id, 'ceiling', e.target.value)}>
                                  {CEILING_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                              </div>
                            </div>

                            {/* Electrics */}
                            <div className="dt-section-label" style={{ marginTop: 10 }}>Electrics</div>
                            <div className="dt-grid-3">
                              <div className="dt-field-sm">
                                <label>Sockets</label>
                                <input className="dt-input-sm" type="number" min="0" value={room.electrics.sockets}
                                  onChange={e => updateElectrics(room.id, 'sockets', parseInt(e.target.value) || 0)} />
                              </div>
                              <div className="dt-field-sm">
                                <label>Light points</label>
                                <input className="dt-input-sm" type="number" min="0" value={room.electrics.lights}
                                  onChange={e => updateElectrics(room.id, 'lights', parseInt(e.target.value) || 0)} />
                              </div>
                              <div className="dt-field-sm">
                                <label>Switches</label>
                                <input className="dt-input-sm" type="number" min="0" value={room.electrics.switches}
                                  onChange={e => updateElectrics(room.id, 'switches', parseInt(e.target.value) || 0)} />
                              </div>
                            </div>

                            {/* Kitchen / Bathroom supply */}
                            {(room.type === 'kitchen' || room.type === 'utility') && (
                              <>
                                <div className="dt-field-sm" style={{ marginTop: 10 }}>
                                  <label>Kitchen supply</label>
                                  <select className="dt-select" value={room.kitchenSupply} onChange={e => updateRoom(room.id, 'kitchenSupply', e.target.value)}>
                                    {KITCHEN_SUPPLY.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                  </select>
                                </div>
                                {room.kitchenSupply === 'contractor' && (
                                  <div className="dt-field-sm" style={{ marginTop: 10 }} data-testid={`room-${room.id}-kitchen-spec`}>
                                    <label>Kitchen spec</label>
                                    <select
                                      className="dt-select"
                                      value={room.kitchenSpec || 'mid'}
                                      onChange={e => updateRoom(room.id, 'kitchenSpec', e.target.value)}
                                    >
                                      {KITCHEN_SPEC.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </select>
                                    <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, lineHeight: 1.4 }}>
                                      {KITCHEN_SPEC.find(o => o.id === (room.kitchenSpec || 'mid'))?.desc}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                            {isWet && room.type !== 'kitchen' && room.type !== 'utility' && (
                              <div className="dt-field-sm" style={{ marginTop: 10 }}>
                                <label>Bathroom supply</label>
                                <select className="dt-select" value={room.bathroomSupply} onChange={e => updateRoom(room.id, 'bathroomSupply', e.target.value)}>
                                  {BATHROOM_SUPPLY.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                              </div>
                            )}

                            {/* Notes */}
                            <div className="dt-field-sm" style={{ marginTop: 10 }}>
                              <label>Notes (optional)</label>
                              <input className="dt-input-sm" placeholder="e.g. bay window, built-in wardrobe..." value={room.notes}
                                onChange={e => updateRoom(room.id, 'notes', e.target.value)} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add room */}
                <div className="dt-add-room" data-testid="add-room-section">
                  <div className="dt-section-label">Add a room</div>
                  <div className="dt-room-presets">
                    {ROOM_PRESETS.map(p => (
                      <button key={p.type} className="dt-preset-btn" onClick={() => addRoom(p.type)} data-testid={`add-${p.type}`}>
                        <span>{p.icon}</span> {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="dt-nav">
                  <button className="dt-btn dt-btn-ghost" onClick={() => goStep(0)}><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button className="dt-btn dt-btn-accent" disabled={!step1Valid} onClick={() => goStep(2)} data-testid="step1-next">
                    Upload plan <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: Upload Plan ═══ */}
        {step === 2 && (
          <div className="dt-screen" data-testid="step-2">
            <div className="dt-card">
              <div className="dt-card-hdr">
                <h2>Upload Floor Plan</h2>
                <p>Upload a floor plan or drawing if you have one. This helps our system verify room dimensions. Optional — you can skip this.</p>
              </div>
              <div className="dt-card-body">
                {!planFile ? (
                  <label className="dt-drop" htmlFor="plan-file" data-testid="plan-upload">
                    <Upload className="w-8 h-8" style={{ color: 'var(--dt-navy)' }} />
                    <p>Click to upload a floor plan</p>
                    <small>PDF, PNG, JPG up to 25MB</small>
                    <input type="file" id="plan-file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: 'none' }}
                      onChange={e => setPlanFile(e.target.files?.[0] || null)} />
                  </label>
                ) : (
                  <div className="dt-file-info">
                    <FileText className="w-5 h-5" style={{ color: 'var(--dt-navy)' }} />
                    <div>
                      <div className="dt-file-name">{planFile.name}</div>
                      <div className="dt-file-size">{(planFile.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button className="dt-link" onClick={() => setPlanFile(null)}>Remove</button>
                  </div>
                )}

                <div className="dt-nav" style={{ marginTop: 20 }}>
                  <button className="dt-btn dt-btn-ghost" onClick={() => goStep(1)}><ArrowLeft className="w-4 h-4" /> Back</button>
                  <button className="dt-btn dt-btn-accent" onClick={() => { goStep(3); handleCalculate(); }} data-testid="step2-next">
                    Generate estimate <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Review & Pay ═══ */}
        {step === 3 && (
          <div className="dt-screen" data-testid="step-3">
            {calculating ? (
              <div className="dt-calc-box">
                <div className="dt-dots"><div /><div /><div /></div>
                <div>Calculating your Schedule of Works...</div>
              </div>
            ) : result ? (
              <>
                <div className="dt-output-card" data-testid="output-card">
                  <div className="dt-out-hdr">
                    <div className="dt-out-label">DETAILED SCHEDULE OF WORKS</div>
                    <div className="dt-out-addr">{addressFull}</div>
                  </div>
                  <div className="dt-out-body">
                    <div className="dt-out-range" data-testid="output-range">
                      <small>£</small>{Math.round(result.total_low / 1000)}<small> – £</small>{Math.round(result.total_high / 1000)}k
                    </div>
                    <div data-testid="dt-range-qualifier" style={{
                      fontSize: 12, color: '#64748B', fontStyle: 'italic',
                      marginTop: 4, marginBottom: 4,
                    }}>
                      dependent on specification and defined scope
                    </div>
                    <div className="dt-out-meta">
                      <span>{totalArea.toFixed(0)} m² GIA</span>
                      <span>{rooms.length} rooms</span>
                      <span>{result.region}</span>
                    </div>

                    {/* Section breakdown */}
                    <div className="dt-sections">
                      <div className="dt-sections-hdr">
                        <span>Work Section</span><span>Low</span><span>High</span>
                      </div>
                      {result.sections?.map((s, i) => (
                        <div key={i} className="dt-section-row">
                          <span>{s.name}</span>
                          <span>£{s.low?.toLocaleString()}</span>
                          <span>£{s.high?.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="dt-section-row total">
                        <span>Total (ex VAT)</span>
                        <span>£{result.total_low?.toLocaleString()}</span>
                        <span>£{result.total_high?.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="dt-out-includes" style={{ marginBottom: 16 }}>
                      <div className="dt-out-inc-title">Your document pack options</div>
                      <p style={{ fontSize: 12, color: '#64748B', marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
                        Pick how much detail you need. Upgrade anytime — you only pay the difference.
                      </p>
                    </div>

                    {/* 3-tier selection */}
                    <div style={{ display: 'grid', gap: 12, marginBottom: 16 }} data-testid="tier-cards">
                      {/* Materials — lowest */}
                      <div style={{ border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '16px 18px', background: '#fff' }} data-testid="tier-card-materials">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2742' }}>Materials Schedule</div>
                            <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.55, marginTop: 4 }}>Know exactly what needs to be purchased before speaking to suppliers.</div>
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#0F2742', whiteSpace: 'nowrap' }}>£29</div>
                        </div>
                        <button
                          style={{ marginTop: 12, width: '100%', background: '#fff', color: '#0F2742', border: '1.5px solid #0F2742', fontSize: 13, fontWeight: 700, padding: '10px 18px', borderRadius: 8, cursor: 'pointer', opacity: checkoutLoading ? 0.5 : 1 }}
                          disabled={checkoutLoading}
                          onClick={() => handleCheckout('scope_materials')}
                          data-testid="tier-buy-materials"
                        >
                          {checkoutLoading && checkoutTier === 'scope_materials' ? 'Starting checkout…' : 'Buy Materials Schedule — £29'}
                        </button>
                      </div>

                      {/* SOW — middle */}
                      <div style={{ border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '16px 18px', background: '#fff' }} data-testid="tier-card-sow">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2742' }}>Schedule of Works</div>
                            <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.55, marginTop: 4 }}>A structured trade-by-trade scope — ready to issue to contractors for pricing.</div>
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: '#0F2742', whiteSpace: 'nowrap' }}>£119</div>
                        </div>
                        <button
                          style={{ marginTop: 12, width: '100%', background: '#fff', color: '#0F2742', border: '1.5px solid #0F2742', fontSize: 13, fontWeight: 700, padding: '10px 18px', borderRadius: 8, cursor: 'pointer', opacity: checkoutLoading ? 0.5 : 1 }}
                          disabled={checkoutLoading}
                          onClick={() => handleCheckout('scope_sow')}
                          data-testid="tier-buy-sow"
                        >
                          {checkoutLoading && checkoutTier === 'scope_sow' ? 'Starting checkout…' : 'Buy Schedule of Works — £119'}
                        </button>
                      </div>

                      {/* Full Project Pack — featured */}
                      <div style={{ border: '2.5px solid #2563EB', borderRadius: 12, padding: '18px 20px', background: 'linear-gradient(180deg,#F0F7FF,#fff)', position: 'relative' }} data-testid="tier-card-full">
                        <div style={{ position: 'absolute', top: -10, right: 16, background: '#2563EB', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 999 }}>Best value</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#0F2742' }}>Full Project Pack</div>
                            <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.55, marginTop: 4 }}>A complete project breakdown — scope, cost, materials and a contractor-hire guide — everything you need before going to tender.</div>
                            <ul style={{ fontSize: 12, color: '#475569', margin: '10px 0 0', padding: '0 0 0 18px', lineHeight: 1.6 }}>
                              <li>Full Schedule of Works by room and trade</li>
                              <li>Cost Estimate with trade-level totals</li>
                              <li>Key Materials Schedule for procurement</li>
                              <li>What To Do Next — contractor questions and comparison guide</li>
                            </ul>
                          </div>
                          <div style={{ fontSize: 24, fontWeight: 800, color: '#0F2742', whiteSpace: 'nowrap' }}>£199</div>
                        </div>
                        <button
                          className="dt-btn dt-btn-accent"
                          style={{ marginTop: 14, width: '100%' }}
                          disabled={checkoutLoading}
                          onClick={() => handleCheckout('scope_full')}
                          data-testid="tier-buy-full"
                        >
                          {checkoutLoading && checkoutTier === 'scope_full' ? 'Starting checkout…' : 'Buy Full Project Pack — £199'}
                        </button>
                      </div>
                    </div>

                    <div className="dt-out-disclaimer">
                      One-off payment · Instant download · Discount codes accepted at checkout · Upgrade anytime, pay only the difference
                    </div>
                  </div>
                </div>

                <div className="dt-disclaimer">
                  <strong>Indicative estimate only.</strong> Figures are based on room-by-room measurements, selected finishes and current UK contractor pricing reflecting current market conditions for your location. Actual costs will vary. This is not a contract document.
                </div>
              </>
            ) : (
              <div className="dt-calc-box"><div>Calculation failed. Please go back and check your room data.</div></div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default DetailedTool;
